import {
  type DataRequirement,
  type DataRequirements,
  REQS,
} from "#src/constants/data-requirements.ts";
import { STEP } from "#src/constants/step.ts";
import { QuestionsService } from "#src/services/http-questions.service.ts";
import {
  defineStore,
  generateInitialState,
  resetState,
  type ToSetupState,
} from "#src/stores/state-wrapper.ts";

import {
  INSURED_TYPES,
  useInsuredStore,
  useJointInsuredStore,
  usePrimaryInsuredStore,
} from "#src/stores/insured.ts";
import { type InsuredType } from "#src/types/Insured.ts";
import { useStepPaymentStore } from "#src/stores/step-payment.ts";
import { useStepExamSchedulingStore } from "#src/stores/step-exam-scheduling.ts";
import { type StoreGeneric, type Pinia } from "pinia";
import { type UnderwritingQuestion } from "#src/composables/underwriting-question.composable.ts";
import { computed } from "vue";
import { type BaseStepStore } from "#src/types/StepStore.ts";
import { getBuild } from "#src/services/http-individuals.service.ts";

export interface InsuredBuild {
  valid: boolean | null;
  exists: boolean | null;
  minWeight: number | null;
  maxWeight: number | null;
  rating: string | null;
  okayToProceed: boolean | null;
}

function initializeInsuredBuild(): InsuredBuild {
  return {
    valid: null,
    exists: null,
    minWeight: null,
    maxWeight: null,
    rating: null,
    okayToProceed: null,
  };
}

export type InsuredChildQuestions = Record<
  UnderwritingQuestion["questionId"],
  UnderwritingQuestion["questionId"][]
>;

export type InsuredQuestions = Record<
  UnderwritingQuestion["questionId"],
  UnderwritingQuestion
>;

export type InsuredRootQuestions = UnderwritingQuestion["questionId"][];

// NOTE(tim): MOVE

export interface SharedRootQuestion {
  label: string;
  insuredQuestionId: UnderwritingQuestion["questionId"];
  jointQuestionId: UnderwritingQuestion["questionId"];
}

function getChildQuestions(questions: Record<string, UnderwritingQuestion>) {
  const childQuestionsByParentId: InsuredChildQuestions = {};
  for (const q in questions) {
    const parentId = questions[q].parentId;

    if (!parentId) continue;
    if (!childQuestionsByParentId[parentId])
      childQuestionsByParentId[parentId] = [];
    childQuestionsByParentId[parentId].push(q);
  }
  return childQuestionsByParentId;
}

function addQuestionsToGroups(
  insuredKey: "insuredQuestionId" | "jointQuestionId",
  questions: Record<string, UnderwritingQuestion>,
  groupPtr: Record<string, Partial<SharedRootQuestion>>
) {
  const uniqueQuestionCounter: Record<string, number> = {};

  for (const q in questions) {
    const question = questions[q];

    if (question.parentId || !question.label) continue;
    if (!uniqueQuestionCounter[question.label])
      uniqueQuestionCounter[question.label] = 0;

    const key = `${question.label}-${uniqueQuestionCounter[question.label]++}`;

    if (!groupPtr[key]) {
      groupPtr[key] = {
        label: question.label,
      } as Partial<SharedRootQuestion>;
    }
    groupPtr[key][insuredKey] = q;
  }
}

function initializeRequiredFields(reqs?: DataRequirements) {
  const required = (requirement: DataRequirement): boolean =>
    reqs?.[requirement] || false;
  return {
    build: required(REQS.BUILD),
  };
}

interface UnderwritingState {
  insuredQuestions: InsuredQuestions;
  insuredRootQuestions: InsuredRootQuestions;
  insuredChildQuestions: InsuredChildQuestions;
  insuredBuild: InsuredBuild;
  requiredFields: Record<string, boolean>;

  jointQuestions: InsuredQuestions;
  jointRootQuestions: InsuredRootQuestions;
  jointChildQuestions: InsuredChildQuestions;
  jointBuild: InsuredBuild;

  sharedRootQuestions: SharedRootQuestion[];
}
interface UnderwritingStep
  extends BaseStepStore,
    ToSetupState<UnderwritingState> {
  getInsuredBuild(insuredKey: InsuredType): Promise<void>;
  clearBuild(insuredKey: InsuredType): void;
  setStepRequirements(srq: DataRequirements): void;
}

function initialState(): UnderwritingState {
  return {
    requiredFields: initializeRequiredFields(),

    insuredQuestions: {},
    insuredRootQuestions: [],
    insuredChildQuestions: {},
    insuredBuild: initializeInsuredBuild(),

    jointQuestions: {},
    jointRootQuestions: [],
    jointChildQuestions: {},
    jointBuild: initializeInsuredBuild(),

    sharedRootQuestions: [],
  };
}

function initializeUnderwritingStep(pinia: Pinia): UnderwritingStep {
  const state = generateInitialState<UnderwritingState>(initialState());

  function $reset() {
    resetState<UnderwritingState>(state, initialState());
  }

  const step = computed(() => STEP.UNDERWRITING);
  const previous = computed(() => {
    const payment = useStepPaymentStore(pinia);
    if (payment.inactive) return payment.previous;
    return payment.step;
  });
  const next = computed(() => {
    const examScheduling = useStepExamSchedulingStore(pinia);
    return examScheduling.step;
  });

  async function beforeLoadAction() {
    const _requiredFields = JSON.parse(
      JSON.stringify(state.requiredFields.value)
    );
    $reset();
    state.requiredFields.value = _requiredFields;

    const args = await new QuestionsService(pinia).getUnderwritingQuestions();

    state.insuredQuestions.value = args.insuredQuestions;
    state.jointQuestions.value = args.jointInsuredQuestions;

    // NOTE(tim): In order to simplify question display for multiple insureds, we group the questions by label.
    //            Currently, Fairway does not provide a way to indicate related questions across insureds.
    //            Additionally, Fairway does not validate label uniqueness across forms leading to intentional duplicates.

    state.insuredChildQuestions.value = getChildQuestions(
      state.insuredQuestions.value
    );
    state.jointChildQuestions.value = getChildQuestions(
      state.jointQuestions.value
    );

    const questionGroups: Record<string, SharedRootQuestion> = {};
    addQuestionsToGroups(
      "insuredQuestionId",
      state.insuredQuestions.value,
      questionGroups
    );
    addQuestionsToGroups(
      "jointQuestionId",
      state.jointQuestions.value,
      questionGroups
    );

    const insured = usePrimaryInsuredStore(pinia);
    const joint = useJointInsuredStore(pinia);

    for (const q in questionGroups) {
      const { insuredQuestionId, jointQuestionId } = questionGroups[q];
      if (insuredQuestionId && jointQuestionId) {
        state.insuredQuestions.value[insuredQuestionId].inputLabel =
          insured.first_name.model;
        state.insuredQuestions.value[insuredQuestionId].label = null;

        state.jointQuestions.value[jointQuestionId].inputLabel =
          joint.first_name.model;
        state.jointQuestions.value[jointQuestionId].label = null;

        state.sharedRootQuestions.value.push(questionGroups[q]);
      } else if (insuredQuestionId) {
        state.insuredRootQuestions.value.push(insuredQuestionId);
      } else if (jointQuestionId) {
        state.jointRootQuestions.value.push(jointQuestionId);
      }
    }

    return Boolean(Object.keys(questionGroups).length);
  }
  function clearBuild(insuredKey: InsuredType) {
    if (insuredKey === INSURED_TYPES.JOINT) {
      state.jointBuild.value = initializeInsuredBuild();
    } else {
      state.insuredBuild.value = initializeInsuredBuild();
    }
  }

  async function getInsuredBuild(insuredKey: InsuredType) {
    const insured = useInsuredStore(insuredKey, pinia);
    const newBuild = await getBuild(pinia, insured.id, {
      height: insured.height.model,
      weight: insured.weight.model,
    });

    if (insuredKey === INSURED_TYPES.JOINT) {
      state.jointBuild.value = newBuild;
    } else {
      state.insuredBuild.value = newBuild;
    }
  }

  function setStepRequirements(srq: DataRequirements) {
    state.requiredFields.value = initializeRequiredFields(srq);
  }

  return {
    ...state,

    getInsuredBuild,
    clearBuild,

    step,
    previous,
    next,
    setStepRequirements,
    beforeLoadAction,
    $reset,
  };
}

export function useStepUnderwritingStore(pinia: Pinia, hot?: StoreGeneric) {
  return defineStore<string, UnderwritingStep>("step-underwriting", () => {
    return initializeUnderwritingStep(pinia);
  })(pinia, hot);
}
